import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useSearchSuggestions} from '@salesforce/commerce-sdk-react'
import {
    Input,
    InputGroup,
    InputRightElement,
    Popover,
    PopoverTrigger,
    PopoverContent,
    Box,
    Flex,
    HStack,
    VStack,
    Text,
    Image,
    Link
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {PopoverArrow, useOutsideClick} from '@chakra-ui/react'
import {Search2Icon} from '@chakra-ui/icons'
import debounce from 'lodash/debounce'
import throttle from 'lodash/throttle'
import {
    suggestionsHeaderStyle,
    phraseLinkStyle,
    featuredHeaderStyle,
    productsRowStyle,
    productCardStyle,
    productImageStyle,
    productNameStyle,
    productPriceStyle,
    productImageMobile,
    productCardMobileStyle,
    containerStyle,
    searchInputStyle,
    inputRightElementStyle,
    popoverDesktopContentStyle,
    popoverArrowStyle,
    mobileDropdownStaticStyle,
    mobileDropdownArrowStaticStyle,
    mobileDropdownBoxStyle,
    wrapperBoxStyle
} from './index.style'
import {pxToRem} from '../../utills/pxToRem'
import {RECENT_SEARCH_MIN_LENGTH} from '@salesforce/retail-react-app/app/constants'
import {capitalize, boldString} from '@salesforce/retail-react-app/app/utils/utils'
import {FormattedNumber, useIntl} from 'react-intl'
import {searchUrlBuilder} from '@salesforce/retail-react-app/app/utils/url'

const SearchInput = (props) => {
    const intl = useIntl()
    const [inputValue, setInputValue] = useState('')
    const [query, setQuery] = useState('')
    const [dropdownTop, setDropdownTop] = useState(0)
    const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false)
    const searchInputRef = useRef(null)
    const popoverContainerRef = useRef(null)

    useOutsideClick({
        ref: popoverContainerRef,
        handler: () => setIsSuggestionsOpen(false)
    })

    const debouncedSetQuery = useRef(debounce((val) => setQuery(val), 300)).current

    const updateDropdownPosition = useCallback(() => {
        if (searchInputRef.current) {
            const rect = searchInputRef.current.getBoundingClientRect()
            setDropdownTop(rect.bottom)
        }
    }, [])

    const throttledPosition = useRef(throttle(updateDropdownPosition, 100)).current

    const suggestionsResult = useSearchSuggestions(
        {
            parameters: {q: query, limit: 3, locale: 'default'}
        },
        {
            enabled: query.length >= RECENT_SEARCH_MIN_LENGTH,
            keepPreviousData: query.length >= RECENT_SEARCH_MIN_LENGTH ? true : false
        }
    )

    const formatResults = useCallback((data, rawInput) => {
        const phrases = (data?.customSuggestions?.suggestedPhrases ?? []).map((p) =>
            boldString(p.phrase, capitalize(rawInput))
        )
        const products = (data?.productSuggestions?.products ?? []).map((item) => ({
            id: item.productId,
            title: boldString(item.productName, capitalize(rawInput)),
            price: item.price || '',
            currency: item.currency || '',
            link: item.c_productURL.replace('/s/Cavenders', ''),
            imageUrl: item.c_productImage?.src || item.c_cloudinary?.url || ''
        }))
        return {phraseResults: phrases, productResults: products}
    }, [])

    const {phraseResults, productResults} = useMemo(() => {
        const raw = searchInputRef.current?.value || ''
        return formatResults(suggestionsResult.data, raw)
    }, [suggestionsResult.data, formatResults])

    const handleInputChange = useCallback(
        (e) => {
            const v = e.target.value
            setInputValue(v)
            const t = v.trim()
            if (t.length >= RECENT_SEARCH_MIN_LENGTH) {
                debouncedSetQuery(t)
            } else {
                debouncedSetQuery.cancel()
                setQuery('')
            }
        },
        [debouncedSetQuery]
    )

    const handleFormSubmit = useCallback(
        (e) => {
            e.preventDefault()
            const term = inputValue.trim()
            if (term.length >= RECENT_SEARCH_MIN_LENGTH) {
                window.location.href = searchUrlBuilder(term)
            }
        },
        [inputValue]
    )

    const handleLink = () => {
        setIsSuggestionsOpen(false)
        setInputValue('')
    }

    useEffect(() => {
        if (isSuggestionsOpen) {
            updateDropdownPosition()
        }
    }, [isSuggestionsOpen, updateDropdownPosition])

    useEffect(() => {
        setIsSuggestionsOpen(inputValue.trim().length >= RECENT_SEARCH_MIN_LENGTH)
    }, [inputValue])

    useEffect(() => {
        window.addEventListener('scroll', throttledPosition, {passive: true})
        window.addEventListener('resize', throttledPosition)
        return () => {
            window.removeEventListener('scroll', throttledPosition)
            window.removeEventListener('resize', throttledPosition)
            throttledPosition.cancel()
            debouncedSetQuery.cancel()
        }
    }, [throttledPosition, debouncedSetQuery])

    return (
        <Box sx={containerStyle} ref={popoverContainerRef}>
            <Popover
                isOpen={isSuggestionsOpen}
                onOpen={updateDropdownPosition}
                onClose={() => setIsSuggestionsOpen(false)}
                arrowSize={13}
                closeOnEsc={false}
                isLazy
                initialFocusRef={searchInputRef}
            >
                <PopoverTrigger>
                    <form onSubmit={handleFormSubmit} autoComplete="off">
                        <HStack columnGap={0}>
                            <InputGroup>
                                <Input
                                    id="search-input"
                                    ref={searchInputRef}
                                    type="search"
                                    autoComplete="off"
                                    autoCorrect="off"
                                    autoCapitalize="off"
                                    spellCheck={false}
                                    value={inputValue}
                                    onChange={handleInputChange}
                                    onKeyDown={(e) =>
                                        e.key === 'Escape' &&
                                        (e.preventDefault(), setIsSuggestionsOpen(false))
                                    }
                                    {...props}
                                    sx={searchInputStyle}
                                />
                                <InputRightElement sx={inputRightElementStyle}>
                                    <Search2Icon boxSize={5} color="white" />
                                </InputRightElement>
                            </InputGroup>
                        </HStack>
                    </form>
                </PopoverTrigger>
                {isSuggestionsOpen && (phraseResults.length || productResults.length) > 0 && (
                    <PopoverContent sx={popoverDesktopContentStyle}>
                        <PopoverArrow {...popoverArrowStyle} />
                        <Box sx={wrapperBoxStyle}>
                            {phraseResults.length > 0 && (
                                <>
                                    <Box sx={suggestionsHeaderStyle}>
                                        {intl.formatMessage({
                                            id: 'header.search.suggestion.suggestions',
                                            defaultMessage: 'SUGGESTIONS'
                                        })}
                                    </Box>
                                    <VStack align="stretch" spacing={0}>
                                        {phraseResults.map((p, i) => (
                                            <Link
                                                key={i}
                                                sx={phraseLinkStyle}
                                                href={searchUrlBuilder(p.replace(/<[^>]+>/g, ''))}
                                                onClick={handleLink}
                                            >
                                                {p.replace(/<[^>]+>/g, '')}
                                            </Link>
                                        ))}
                                    </VStack>
                                </>
                            )}
                            {productResults.length > 0 && (
                                <>
                                    <Box sx={featuredHeaderStyle}>
                                        {intl.formatMessage({
                                            id: 'header.search.suggestion.featured_products',
                                            defaultMessage: 'FEATURED PRODUCTS'
                                        })}
                                    </Box>
                                    <Flex sx={productsRowStyle}>
                                        {productResults.map((prod) => {
                                            const title = prod.title.replace(/<[^>]+>/g, '')
                                            return (
                                                <Link
                                                    key={prod.id}
                                                    sx={productCardStyle}
                                                    href={prod.link}
                                                    onClick={handleLink}
                                                >
                                                    <Image
                                                        sx={productImageStyle}
                                                        src={prod.imageUrl}
                                                        alt={title}
                                                    />
                                                    <Text sx={productNameStyle}>{title}</Text>
                                                    <Text sx={productPriceStyle}>
                                                        <FormattedNumber
                                                            value={prod.price}
                                                            style="currency"
                                                            currency={prod.currency}
                                                        />
                                                    </Text>
                                                </Link>
                                            )
                                        })}
                                    </Flex>
                                </>
                            )}
                        </Box>
                    </PopoverContent>
                )}
                {isSuggestionsOpen && (phraseResults.length || productResults.length) > 0 && (
                    <Box sx={{...mobileDropdownStaticStyle, top: `${dropdownTop + 10}px`}}>
                        <Box sx={mobileDropdownArrowStaticStyle} />
                        <Box sx={mobileDropdownBoxStyle}>
                            {phraseResults.length > 0 && (
                                <>
                                    <Box sx={suggestionsHeaderStyle}>
                                        {intl.formatMessage({
                                            id: 'header.search.suggestion.suggestions',
                                            defaultMessage: 'SUGGESTIONS'
                                        })}
                                    </Box>
                                    <VStack align="stretch" spacing={pxToRem(8)} mb={pxToRem(16)}>
                                        {phraseResults.map((p, i) => (
                                            <Link
                                                key={i}
                                                sx={phraseLinkStyle}
                                                href={searchUrlBuilder(p.replace(/<[^>]+>/g, ''))}
                                                onClick={handleLink}
                                            >
                                                {p.replace(/<[^>]+>/g, '')}
                                            </Link>
                                        ))}
                                    </VStack>
                                </>
                            )}
                            {productResults.length > 0 && (
                                <>
                                    <Box sx={featuredHeaderStyle}>
                                        {intl.formatMessage({
                                            id: 'header.search.suggestion.featured_products',
                                            defaultMessage: 'FEATURED PRODUCTS'
                                        })}
                                    </Box>
                                    <VStack spacing={pxToRem(16)} mt={pxToRem(22)}>
                                        {productResults.map((prod) => {
                                            const title = prod.title.replace(/<[^>]+>/g, '')
                                            return (
                                                <Link
                                                    key={prod.id}
                                                    sx={productCardMobileStyle}
                                                    href={prod.link}
                                                    onClick={handleLink}
                                                >
                                                    <Image
                                                        sx={productImageMobile}
                                                        src={prod.imageUrl}
                                                        alt={title}
                                                    />
                                                    <Box ml={pxToRem(20)} mt={pxToRem(6)} flex={1}>
                                                        <Text sx={productNameStyle} noOfLines={2}>
                                                            {title}
                                                        </Text>
                                                        <Text sx={productPriceStyle}>
                                                            <FormattedNumber
                                                                value={prod.price}
                                                                style="currency"
                                                                currency={prod.currency}
                                                            />
                                                        </Text>
                                                    </Box>
                                                </Link>
                                            )
                                        })}
                                    </VStack>
                                </>
                            )}
                        </Box>
                    </Box>
                )}
            </Popover>
        </Box>
    )
}

SearchInput.displayName = 'SearchInput'
export default SearchInput
